import { forwardRef, useState } from "react";
import { useObjectRef } from "react-aria";
import {
  type InputProps as InputPrimitiveProps,
  TextField as TextFieldPrimitive,
  type TextFieldProps as TextFieldPrimitiveProps,
} from "react-aria-components";
import {
  CorePasswordInput,
  type PropsWithOptionalTestID,
} from "@boi/components-core";

import * as S from "./PasswordInput.styled";
import { PasswordInputButton } from "./PasswordInputButton";
import { InputWrapper } from "../InputWrapper";
import { InputAlerts, type InputAlertsProps } from "../InputAlerts";
import { InputLabelTopV2, type InputLabelTopV2Props } from "../InputLabelTopV2";
import { OuterFieldV2 } from "../OuterFieldV2";

type InputType = CorePasswordInput.InputType;

export type PasswordInputProps = PropsWithOptionalTestID &
  Pick<InputPrimitiveProps, "placeholder"> &
  Omit<TextFieldPrimitiveProps, "type" | "children"> & {
    labelProps?: Omit<InputLabelTopV2Props, "children">;
    alerts?: InputAlertsProps["alerts"];
    withTooltip?: boolean;
  };

const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  (props, forwardedRef) => {
    const {
      labelProps,
      alerts,
      placeholder,
      withTooltip = false,
      testID = "password-input",
      ...otherProps
    } = props;

    const ref = useObjectRef(forwardedRef);

    const [type, setType] = useState<InputType>("password");

    function focusElement() {
      ref.current?.focus();
    }

    return (
      <TextFieldPrimitive {...otherProps} type={type}>
        {({ isDisabled }) => (
          <InputWrapper alert={alerts && <InputAlerts alerts={alerts} />}>
            <InputLabelTopV2 {...labelProps}>
              <OuterFieldV2
                buttonSlot={
                  !isDisabled && (
                    <PasswordInputButton
                      type={type}
                      setType={setType}
                      withTooltip={withTooltip}
                    />
                  )
                }
                focusElement={focusElement}
              >
                <S.Input
                  ref={ref}
                  placeholder={placeholder}
                  data-testid={testID}
                />
              </OuterFieldV2>
            </InputLabelTopV2>
          </InputWrapper>
        )}
      </TextFieldPrimitive>
    );
  }
);

PasswordInput.displayName = "PasswordInput";

export default PasswordInput;
